import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// Actions
import { get_organization_timezone } from '../redux/actions/organizationActions';

// Utils
import { DEFAULT_TIMEZONE, resolve_org_timezone } from '../utils/timezone_handler';

export const ORG_TIMEZONE_STORAGE_KEY = 'organization_timezone';
export const ORG_TIMEZONE_FALLBACK_STORAGE_KEY = 'organization_timezone_fallback';

/*
	Resolves the timezone of the given organization, which is not always the one of the user:
	an admin can select a different organization on the filters and the cycle must respect it.

	Resolution order:
		1. Timezone cache indexed by org id (no request)
		2. Organization of the user, already fetched by the Header (redux, then localStorage)
		3. Any other organization: requested once and cached
		4. Default timezone
*/
const useOrgTimezone = (orgId) => {
	const dispatch = useDispatch();

	const { user } = useSelector(state => state.auth);

	const { organization_info, organization_timezones } = useSelector(state => state.organization);

	// Keeps track of the requests already sent to avoid firing them on every render
	const requestedOrgs = useRef({});

	const cached = orgId ? organization_timezones[orgId] : null;

	const isUserOrg = Boolean(orgId) && orgId === user?.organization;

	useEffect(() => {
		if (!orgId || cached || isUserOrg) return;

		if (!requestedOrgs.current[orgId]) {
			requestedOrgs.current[orgId] = true;
			dispatch(get_organization_timezone(orgId));
		}
	}, [dispatch, orgId, cached, isUserOrg]);

	if (cached) {
		return { ...cached, loading: false };
	}

	if (isUserOrg) {
		// The Header request may still be in flight after a hard reload
		if (organization_info?._id?.$oid === orgId) {
			return { ...resolve_org_timezone(organization_info), loading: false };
		}

		const stored = localStorage.getItem(ORG_TIMEZONE_STORAGE_KEY);
		if (stored) {
			return {
				timezone: stored,
				is_fallback: localStorage.getItem(ORG_TIMEZONE_FALLBACK_STORAGE_KEY) === 'true',
				loading: false
			};
		}

		return { timezone: DEFAULT_TIMEZONE, is_fallback: true, loading: true };
	}

	return {
		timezone: DEFAULT_TIMEZONE,
		is_fallback: true,
		// While the timezone of another organization is being fetched the form must not be submitted
		loading: Boolean(orgId)
	};
}

export default useOrgTimezone;
